// 牌選択パレットで表示するための、34種の牌IDのリスト
export const HAI_TYPES = {
  MANZU: [0, 8], // 萬子 1、9
  PINZU: [9, 10, 11, 12, 13, 14, 15, 16, 17], // 筒子 1-9
  SOUZU: [18, 19, 20, 21, 22, 23, 24, 25, 26], // 索子 1-9
  JIHAI: [27, 28, 29, 30, 31, 32, 33], // 字牌 東南西北白發中
  AKA: [35, 36], // 赤5筒、赤5索
};

export const ALL_HAIS = [
  ...HAI_TYPES.MANZU,
  ...HAI_TYPES.PINZU,
  ...HAI_TYPES.SOUZU,
  ...HAI_TYPES.JIHAI,
  ...HAI_TYPES.AKA,
];

/**
 * 牌IDを対応する牌画像パスに変換する
 * @param {number} haiId 牌ID（0-36）
 * @returns {string} 牌画像パス
 */
export const getHaiImageSrc = (haiId) => {
  if (haiId === null || haiId === undefined) return '';
  return `/hais/${haiId}.png`;
};

export const isValidHai = (haiId) => {
  if (haiId === null || haiId === undefined) {
    return '牌IDが指定されていません';
  }
  if (ALL_HAIS.includes(haiId) === false) {
    return `牌IDが不正です: ${haiId}`;
  }
  
  return null;
}

export const isValidTehai = (haiIdList) => {
  if (!haiIdList || haiIdList.length === 0) {
    // 手牌が一つもない場合
    return '手牌が指定されていません';
  }
  if (haiIdList.length < 2 || haiIdList.length > 14) {
    // 手牌の数が2～14の範囲外はエラー
    return `手牌が牌数が正しくありません: ${haiIdList.length}`;
  }
  for (const haiId of haiIdList) {
    // 無効な牌IDはエラー
    const validHaiMessage = isValidHai(haiId);
    if (validHaiMessage !== null) {
      return validHaiMessage;
    }
  }
  return null;
}

export const isValidFuro = (haiIdList) => {
  if (!haiIdList || haiIdList.length === 0) {
    // 副露が一つもない場合
    return null;
  }
  if (haiIdList.length !== 3 && haiIdList.length !== 4) {
    // 副露の牌数が3，4以外の場合
    return `副露の牌数が正しくありません: ${haiIdList.length}`;
  }
  for (const haiId of haiIdList) {
    const validHaiMessage = isValidHai(haiId);
    if (validHaiMessage !== null) {
      // 副露に牌内に不正な牌IDが含まれている場合
      return validHaiMessage;
    }
  }
  if (haiIdList.length === 3) {
    if (haiIdList[0] === haiIdList[1] && haiIdList[1] === haiIdList[2]) {
      // 刻子
      return null;
    }
    // 順子の妥当性
    const sortedHaiList = sortHais(haiIdList).map(haiIdToLocalHai);
    if (sortedHaiList.some(hai => sortedHaiList[0].shurui !== hai.shurui)) {
      // 牌種が異なる場合
      return `順子の構成が不正です: ${haiIdList.join(', ')}`;
    }
    const haishu = sortedHaiList[0].shurui;
    if ([0, 1, 2].includes(haishu) === false) {
      // 萬子，筒子，索子以外の場合
      return `順子の構成が不正です: ${haiIdList.join(', ')}`;
    }
    if (sortedHaiList[0].number + 1 === sortedHaiList[1].number && sortedHaiList[1].number + 1 === sortedHaiList[2].number) {
      // 正常な順子の並び
      return null;
    } else {
      // 数牌が連続になっていない場合
      return `順子の構成が不正です: ${haiIdList.join(', ')}`;
    }
  } else {
    // 槓子
    if (haiIdList.some(haiId => haiIdList[0] !== haiId)) {
      return `槓子の構成が不正です: ${haiIdList.join(', ')}`;
    }
  }

  return null;
}

export const isValidFuros = (haiIdLists) => {
  if (!haiIdLists || haiIdLists.length !== 4) {
    return '副露が指定されていません';
  }
  for (const [i, haiIdList] of haiIdLists.entries()) {
    const validFuroMessage = isValidFuro(haiIdList);
    if (validFuroMessage !== null) {
      return `副露${i + 1}が不正です: ${validFuroMessage}`;
    }
  }
  return null;
}

export const haiIdToLocalHai = (haiId) => {
  const validHaiMessage = isValidHai(haiId);
  if (validHaiMessage !== null) {
    throw Error(validHaiMessage);
  }
  const isAka = HAI_TYPES.AKA.includes(haiId);
  const normalHaiId = isAka ? [4, 13, 22][haiId - 34] : haiId;
  return {
    id: haiId,
    shurui: Math.floor(normalHaiId / 9),
    number: normalHaiId % 9 + 1,
    normalId: normalHaiId,
    aka: isAka,
  };
}

/**
 * 牌IDをソートで使用する値に変換する
 * 赤牌を通常牌に`0.5`を足した値にし、
 * 数牌の隣に赤牌が並ぶようにすることを目的とする
 * @param {number} haiId 牌ID
 * @returns {number} ソートで使用する value
 */
const sortHaiValue = (haiId) => {
  return {
    [HAI_TYPES.AKA[0]]: HAI_TYPES.PINZU[4] + 0.5,
    [HAI_TYPES.AKA[1]]: HAI_TYPES.SOUZU[4] + 0.5,
  }[haiId] ?? haiId;
}

/**
 * 牌IDの配列を麻雀の標準的な順序でソートします。
 * @param {number[]} hais - 牌IDの配列
 * @returns {number[]} ソートされた新しい牌IDの配列
 */
export const sortHais = (hais) => {
  return [...hais].sort((a, b) => sortHaiValue(a) - sortHaiValue(b));
}